<template>
  <div class="row">
    <div class="col-md-3">
      <form class="needs-validation" ref="form" id="form">
        <div class="form-group">
          <label class="mb-0" for="Operator">Operator: </label>
          <select class="form-control" v-model="operator" required>
            <option
              v-for="operator in operators"
              :key="operator.id"
              :value="operator.id"
            >
              {{ operator.username }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label class="mb-0" for="BatchNumber">Batch Nummer: </label>
          <input
            type="text"
            class="form-control"
            v-model="batchNumber"
            id="BatchNumber"
            required
          />
        </div>

        <div class="mb-3 form-group">
          <label class="mb-0" for="BatchExpiryDate">{{
            datePickerLabel
          }}</label>
          <VueDatePicker
            class="b-form-datepicker"
            v-model="expiryDate"
            :enable-time-picker="false"
            required
            >{{ expiryDate }}</VueDatePicker
          >
        </div>

        <div class="mb-2 form-check">
          <input
            class="form-check-input"
            type="checkbox"
            v-model="isStored"
            value=""
            id="isStored"
          />
          <label class="form-check-label" for="isStored">
            Rückstellmuster bereit
          </label>
        </div>

        <div
          class="mb-1 form-check"
          v-for="(material, id) in materials"
          :key="id"
        >
          <input
            class="form-check-input"
            type="radio"
            name="materials"
            :id="'radio' + id"
            v-model="chosenMat"
            :value="material"
            @change="disableInputField"
          />
          <label class="form-check-label" :for="'radio' + id">
            {{ material.name }}
          </label>
        </div>

        <div class="mb-2 form-check">
          <input
            class="form-check-input"
            type="radio"
            name="materials"
            id="radioOther"
            value="true"
            @change="enableInputField"
          />
          <label class="form-check-label" for="radioOther"> andere </label>
          <input
            class="form-control"
            type="text"
            name="materials"
            v-model="chosenMat"
            id="inputField"
            hidden
          />
        </div>

        <button type="button" class="btn btn-primary mb-3" @click="submit()">
          Speichern
        </button>
      </form>

      <transition name="alert">
        <div
          class="alert alert-success mt-1"
          role="alert"
          v-if="triggerSuccess"
        >
          Der Eintrag wurde gespeichert!
        </div>
      </transition>

      <transition name="alert">
        <div class="alert alert-danger mt-1" role="alert" v-if="triggerError">
          Eintrag konnte nicht gespeichert werden!
        </div>
      </transition>

      <transition name="alert">
        <div
          class="alert alert-warning mt-1"
          role="alert"
          v-if="triggerWarning"
        >
          Bitte füllen Sie alle Felder aus!
        </div>
      </transition>

      <transition name="alert">
        <div class="alert alert-warning mt-1" role="alert" v-if="triggerAbort">
          Vorgang abgebrochen!
        </div>
      </transition>

      <transition name="alert">
        <div
          class="alert alert-warning mt-1"
          role="alert"
          v-if="triggerBackSheetWarning"
        >
          Geben Sie im Datumsfeld das Produktionsdatum der Backsheet Folie an!
        </div>
      </transition>
    </div>

    <div class="col-md-9">
      <FoilCutterTable :url="props.tableUrl" :key="componentKey" />
    </div>

    <transition name="modal">
      <template v-if="showModal">
        <MyModal
          msg="Das Ablaufdatum der Folie wurde bereits überschritten. Trotzdem speichern?"
          :visible="showModal"
        >
          <template #success>
            <button
              type="button"
              id="success"
              class="btn btn-success"
              @click="handleModalButton(true)"
            >
              Speichern
            </button>
          </template>
          <template #declined>
            <button
              type="button"
              id="declined"
              class="btn btn-warning"
              @click="handleModalButton(false)"
            >
              Abbrechen
            </button>
          </template>
        </MyModal>
      </template>
    </transition>
  </div>
</template>

<script setup>
import { onMounted, ref, defineProps, watch } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import FoilCutterTable from "@/components/foilcutter/FoilCutterTable.vue";
import MyModal from "@/components/helpers/MyModal.vue";
import moment from "moment-timezone";
import { instance as axios } from "@/services/axios";

const props = defineProps({
  stationId: {
    type: Number,
    required: true,
  },
  tableUrl: {
    type: String,
    required: true,
  },
  isBackSheet: {
    type: Boolean,
    required: false,
  },
});

const getRequestJobs = `/User/GetUsersByJobtype/2`;
const getRequestAllFoilTypes = `/Material/GetAllFoilTypes`;
const postRequestMaterialFoilUrl = `/Material/AddMaterialFoil`;

const componentKey = ref(0);

let operator = ref();
let chosenMat = ref();
let batchNumber = ref("");
let isStored = ref(false);
let expiryDate = ref();
let datePickerLabel = ref("Batch Ablauf Datum: ");
watch(
  () => chosenMat.value,
  (newValue) => {
    if (newValue && newValue.standardfoil === 18) {
      datePickerLabel.value = "BackSheet Produktionsdatum: ";
      triggerBackSheetWarning.value = true;
      setTimeout(() => (triggerBackSheetWarning.value = false), 5000);
    } else {
      datePickerLabel.value = "Batch Ablauf Datum: ";
    }
  }
);

const operators = ref([]);
const materials = ref([]);

let triggerSuccess = ref(false);
let triggerError = ref(false);
let triggerWarning = ref(false);
let triggerAbort = ref(false);
let triggerBackSheetWarning = ref(false);

let showModal = ref(false);
let modalResolve;

onMounted(() => {
  axios
    .get(getRequestJobs)
    .then(({ data }) => {
      operators.value = data
        .map(({ id, username }) => ({ id, username }))
        .sort((a, b) => a.username.localeCompare(b.username));
    })
    .catch((error) => console.log(error.message));

  fetchMaterials();
});

watch(batchNumber, (newValue) => {
  if (newValue && newValue.includes("$")) {
    batchNumber.value = newValue.slice(newValue.indexOf("$") + 1);
  }
});
document.addEventListener("keydown", (event) => {
  if (event.key === "$") {
    batchNumber.value = "";
  }
});

function enableInputField() {
  chosenMat.value = null;
  document.getElementById("inputField").hidden = false;
}

function disableInputField() {
  document.getElementById("inputField").hidden = true;
}

const handleModal = () => {
  showModal.value = true;
  return new Promise((resolve) => {
    modalResolve = resolve;
  });
};

const handleModalButton = (value) => {
  showModal.value = false;
  modalResolve(value);
};

const fetchMaterials = () => {
  axios
    .get(getRequestAllFoilTypes)
    .then(({ data }) => {
      materials.value = data
        .filter(
          ({ standardfoil }) =>
            standardfoil === 1 || standardfoil === props.stationId
        )
        .map(({ id, name, standardfoil }) => ({ id, name, standardfoil }));
    })
    .catch((error) => console.log(error.message));
};

function resetForm() {
  operator.value = null;
  chosenMat.value = null;
  batchNumber.value = "";
  isStored.value = false;
  expiryDate.value = null;
  componentKey.value += 1;
  fetchMaterials();
  disableInputField();
  document.getElementById("radioOther").checked = false;
}

async function submit() {
  //checks form validity, because standard form validation doesn't work due to incompatibilities of bootstrap/vue3
  if (
    !document.getElementById("form").checkValidity() ||
    chosenMat.value == null ||
    expiryDate.value == null
  ) {
    //warning alert
    triggerWarning.value = true;
    setTimeout(() => (triggerWarning.value = false), 5000);
    return;
  }

  const currentDate = moment().tz("Europe/Zurich").format();
  let expiryDateConverted = new Date(expiryDate.value);
  if (chosenMat.value.standardfoil === 18) {
    expiryDateConverted.setMonth(expiryDateConverted.getMonth() + 12);
  }
  expiryDateConverted = expiryDateConverted
    .toISOString()
    .split("T", 1)
    .toString();

  if (expiryDateConverted < currentDate) {
    const modalResult = await handleModal();
    if (!modalResult) {
      //abort alert
      triggerAbort.value = true;
      setTimeout(() => (triggerAbort.value = false), 5000);
      return;
    }
  }

  // let jsonObject = {
  //   dateinproduction: currentDate,
  //   operatorid: operator.value,
  //   foilbatch: batchNumber.value,
  //   foilexpirationdate: expiryDateConverted,
  //   isfoilsamplestored: isStored.value,
  //   foilthickness: null,
  //   stationid: props.stationId.toString(),
  //   foiltypeNavigation: {
  //     name: chosenMat.value.name ? chosenMat.value.name : chosenMat.value,
  //     standardfoil: chosenMat.value.standardfoil
  //       ? chosenMat.value.standardfoil
  //       : props.stationId,
  //   },
  // };
  let jsonObject = {
    dateInProduction: currentDate,
    operatorid: operator.value,
    foilbatch: batchNumber.value,
    foilExpirationDate: new Date(expiryDateConverted),
    isFoilSampleStored: isStored.value,
    foilThickness: null,
    stationid: props.stationId,
    name: chosenMat.value.name ? chosenMat.value.name : chosenMat.value,
    standardfoil: chosenMat.value.standardfoil
      ? chosenMat.value.standardfoil
      : props.stationId,
  };
  try {
    await axios.post(postRequestMaterialFoilUrl, jsonObject);
  } catch (e) {
    console.log(e.message);
    //error alert
    triggerError.value = true;
    setTimeout(() => (triggerError.value = false), 5000);
    resetForm();
    return;
  }
  //success alert
  triggerSuccess.value = true;
  setTimeout(() => (triggerSuccess.value = false), 5000);

  resetForm();

  fetchMaterials();
}
</script>

<style>
.alert-enter-active,
.alert-leave-active,
.modal-enter-active {
  transition: opacity 0.5s ease;
}
.alert-enter-from,
.alert-leave-to,
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

input[type="radio"] {
  transform: scale(1.3);
}
input[type="checkbox"] {
  transform: scale(1.2);
}
</style>
